import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { signIn, signUp, resetPassword } from '../AuthContext';
import { fetchPlans } from '../data/billing';
import { fetchAgreements, stashPendingAcceptance } from '../data/agreements';
import Clickwrap from './Clickwrap';
import Icon from './Icons';
import './Signup.css';

// Firebase auth errors carry machine codes and sometimes raw JSON in
// .message — never show those to the user.
function friendlyAuthError(err, mode) {
  const code = err?.code || '';
  if (code.includes('invalid-login-credentials') || code.includes('wrong-password') || code.includes('user-not-found') || code.includes('invalid-credential')) {
    return "Incorrect email or password. If you don't have an account yet, use “Create one” below.";
  }
  if (code.includes('email-already-in-use')) return 'An account with this email already exists — sign in instead.';
  if (code.includes('weak-password')) return 'Password must be at least 6 characters.';
  if (code.includes('invalid-email')) return "That email address doesn't look right.";
  if (code.includes('too-many-requests')) return 'Too many attempts — please wait a minute and try again.';
  if (code.includes('network-request-failed')) return 'Network error — check your connection and try again.';
  if (mode === 'reset') return 'Could not send the reset email — please try again.';
  return mode === 'signup' ? 'Could not create the account — please try again.' : 'Sign-in failed — please try again.';
}

const fmtUsd = (cents) =>
  cents == null
    ? '—'
    : `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 })}`;
const fmtTokens = (n) => (n >= 1e6 ? `${n / 1e6}M` : n >= 1e3 ? `${Math.round(n / 1e3)}K` : String(n));

export default function Login() {
  // Invite links land here as /login?email=…&invited=1 (a bare /?invited=1
  // is redirected here). Prefill the address and start on "create account"
  // so the invitee's path is type-password-done.
  // Landing CTAs arrive as /login?signup=1[&plan=team&interval=year&seats=2]:
  // signup renders the full-page layout with the plan configurator, and the
  // selection is kept in the URL so the post-auth ?next= redirect carries it
  // to /pricing for checkout.
  const [params, setParams] = useSearchParams();
  const invitedEmail = params.get('invited') ? (params.get('email') || '') : '';
  const wantsSignup = !!invitedEmail || params.get('signup') === '1';
  const [mode, setMode] = useState(wantsSignup ? 'signup' : 'signin'); // 'signin' | 'signup' | 'reset'
  const [email, setEmail] = useState(invitedEmail || params.get('email') || '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  // Signup clickwrap: the terms + privacy policy the person must read to
  // the end before the account is created (null = not yet reviewing).
  const [docs, setDocs] = useState(null);

  // Plan configurator state (full-page signup only). null plan = start on the
  // signup trial and decide later.
  const planKey = params.get('plan') || null;
  const annual = params.get('interval') === 'year';
  const seats = Math.max(0, Math.min(500, parseInt(params.get('seats') || '0', 10) || 0));
  const [catalog, setCatalog] = useState(null);
  const fullPage = mode === 'signup' && !invitedEmail;

  useEffect(() => {
    if (fullPage) fetchPlans().then(setCatalog).catch(() => setCatalog(null));
  }, [fullPage]);

  const plans = useMemo(
    () => Object.entries(catalog?.plans || {}).map(([key, p]) => ({ key, ...p })).sort((a, b) => (a.amountCents || 0) - (b.amountCents || 0)),
    [catalog]
  );
  const plan = planKey ? plans.find((p) => p.key === planKey) : null;

  // Selection lives in the URL: the /login route's post-auth redirect reads
  // ?next=, so changing the plan here changes where checkout resumes.
  const select = (nextPlan, nextAnnual = annual, nextSeats = seats) => {
    const q = new URLSearchParams(params);
    q.set('signup', '1');
    if (nextPlan) {
      q.set('plan', nextPlan);
      q.set('interval', nextAnnual ? 'year' : 'month');
      if (nextSeats > 0) q.set('seats', String(nextSeats)); else q.delete('seats');
      q.set('next', `/pricing?plan=${nextPlan}&interval=${nextAnnual ? 'year' : 'month'}${nextSeats > 0 ? `&seats=${nextSeats}` : ''}`);
    } else {
      q.delete('plan'); q.delete('seats'); q.delete('interval'); q.delete('next');
    }
    setParams(q, { replace: true });
  };

  const switchMode = (m) => {
    setMode(m);
    setDocs(null);
    setError(null);
    setNotice(null);
  };

  // Signup step 2: the account is created only after the agreements were
  // read to the end. The accepted ids are parked in sessionStorage because
  // this component unmounts the moment the account exists; ClickwrapGate
  // records them against the new uid.
  const finishSignup = async (seen) => {
    setError(null);
    setBusy(true);
    try {
      stashPendingAcceptance(seen.map((d) => d.id));
      await signUp(email, password);
    } catch (err) {
      setError(friendlyAuthError(err, 'signup'));
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      if (mode === 'reset') {
        await resetPassword(email);
        setNotice(`Reset link sent to ${email}. Check your inbox (and spam), then sign in with your new password.`);
      } else if (mode === 'signup') {
        // Step 1: validate the form, then show the agreements to read.
        const list = await fetchAgreements();
        if (!list.length) throw new Error('agreements unavailable');
        setDocs(list);
      } else {
        await signIn(email, password);
      }
    } catch (err) {
      // Don't reveal whether an account exists on reset attempts.
      if (mode === 'reset' && (err?.code || '').includes('user-not-found')) {
        setNotice(`Reset link sent to ${email}. Check your inbox (and spam), then sign in with your new password.`);
      } else if (mode === 'signup' && !(err?.code || '').startsWith('auth/')) {
        setError('The terms and privacy policy could not be loaded — please try again in a moment.');
      } else {
        setError(friendlyAuthError(err, mode));
      }
    } finally {
      setBusy(false);
    }
  };

  const form = (
    <>
      {error && (
        <div className="cs-empty" style={{ padding: 12, marginBottom: 14, color: 'var(--danger)' }}>
          {error}
        </div>
      )}
      {notice && (
        <div className="cs-empty" style={{ padding: 12, marginBottom: 14 }}>
          {notice}
        </div>
      )}

      {docs ? (
        <>
          <Clickwrap
            docs={docs}
            busy={busy}
            intro={`Read to the end, then agree to create the account for ${email}: the terms of service and privacy policy.`}
            onAgree={finishSignup}
          />
          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setDocs(null)} disabled={busy}>Back</button>
          </div>
        </>
      ) : (
      <form onSubmit={submit}>
        <div className="cs-field">
          <label>Email</label>
          <input
            type="email"
            className="cs-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
          />
        </div>
        {mode !== 'reset' && (
          <div className="cs-field">
            <label>Password</label>
            <input
              type="password"
              className="cs-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
            />
          </div>
        )}
        <button type="submit" className="cs-btn cs-btn-primary" style={{ width: '100%' }} disabled={busy}>
          {busy ? 'Please wait…' : mode === 'signup' ? 'Review agreements and create account' : mode === 'reset' ? 'Send reset link' : 'Sign in'}
        </button>
        {mode === 'signup' && (
          <p className="cs-help" style={{ marginTop: 8, textAlign: 'center' }}>
            Next: read the <a href="/terms" target="_blank" rel="noreferrer">terms of service</a> and{' '}
            <a href="/privacy" target="_blank" rel="noreferrer">privacy policy</a>; the account is created when you agree.
          </p>
        )}
      </form>
      )}

      {!docs && (
      <div className="u-stack" style={{ textAlign: 'center', marginTop: 16, '--gap': 'var(--sp-1)' }}>
        {mode === 'signin' && (
          <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => switchMode('reset')}>
            Forgot password?
          </button>
        )}
        <button
          className="cs-btn cs-btn-ghost cs-btn-sm"
          onClick={() => switchMode(mode === 'signin' ? 'signup' : 'signin')}
        >
          {mode === 'signin'
            ? 'Need an account? Create one'
            : 'Already have an account? Sign in'}
        </button>
      </div>
      )}
    </>
  );

  // ---- Full-page signup: plan configurator + order summary + account form ----
  if (fullPage) {
    const monthly = plan ? (annual ? plan.amountCentsAnnual : plan.amountCents) : 0;
    const seatPrice = plan ? (annual ? plan.seatOverageCentsAnnual : plan.seatOverageCents) : 0;
    const total = monthly + seats * (seatPrice || 0);
    const trial = catalog?.trial;
    const planTrialDays = plan?.trialDays || 0;
    const seatsIncluded = typeof plan?.entitlements?.maxSeats === 'number' ? plan.entitlements.maxSeats : null;

    return (
      <div className="fl-signup">
        <div className="fl-signup-brand">
          <span className="cs-brand">fast<span>.legal</span></span>
          <span className="fl-signup-tag">{docs ? 'Agreements' : 'Create your workspace'}</span>
        </div>

        {docs ? (
          <section className="fl-signup-form" style={{ maxWidth: 880, margin: '0 auto' }}>
            <h2>Before your account is created</h2>
            {form}
          </section>
        ) : (
        <div className="fl-signup-grid">
          {/* Plan configurator */}
          <section className="fl-signup-plans">
            <div className="fl-signup-plans-head">
              <h2>Pick your plan</h2>
              <div className="fl-interval" role="group" aria-label="Billing interval">
                <button type="button" className={annual ? '' : 'on'} onClick={() => select(planKey, false)}>Monthly</button>
                <button type="button" className={annual ? 'on' : ''} onClick={() => select(planKey, true)}>
                  Annual <span className="fl-interval-save">save 20%</span>
                </button>
              </div>
            </div>

            {trial && (
              <button
                type="button"
                className={`fl-signup-trialcard${!planKey ? ' selected' : ''}`}
                onClick={() => select(null)}
              >
                <span className="fl-signup-radio" aria-hidden="true" />
                <span className="fl-signup-plancopy">
                  <strong>Decide later — start with the free trial</strong>
                  <span>
                    Every new workspace gets {trial.days} days of {catalog?.plans?.[trial.planKey]?.name || 'Team'} features
                    with a {fmtTokens(trial.includedTokens || 0)}-token AI budget. No credit card.
                  </span>
                </span>
              </button>
            )}

            {plans.length === 0 && <div className="cs-skeleton" style={{ height: 220 }} />}
            {plans.map((p) => {
              const price = annual ? p.amountCentsAnnual : p.amountCents;
              const selected = planKey === p.key;
              return (
                <button
                  key={p.key}
                  type="button"
                  className={`fl-signup-plancard${selected ? ' selected' : ''}`}
                  onClick={() => select(p.key)}
                >
                  <span className="fl-signup-radio" aria-hidden="true" />
                  <span className="fl-signup-plancopy">
                    <strong>
                      {p.name}
                      {p.trialDays > 0 && <em className="fl-signup-trialchip">{p.trialDays}-day free trial</em>}
                    </strong>
                    <span>{p.tagline}</span>
                    <span className="fl-signup-planmeta">
                      {typeof p.entitlements?.maxSeats === 'number' ? `${p.entitlements.maxSeats} seat${p.entitlements.maxSeats === 1 ? '' : 's'}` : 'Unlimited seats'}
                      {' · '}
                      {typeof p.entitlements?.maxProjects === 'number' ? `${p.entitlements.maxProjects} projects` : 'unlimited projects'}
                      {' · '}
                      {fmtTokens(p.entitlements?.includedTokens || 0)} tokens/mo
                    </span>
                  </span>
                  <span className="fl-signup-planprice">
                    {p.fromPrice && <em>from </em>}{fmtUsd(price)}<span>/mo</span>
                  </span>
                </button>
              );
            })}

            {/* Seat stepper for the selected plan */}
            {plan && seatsIncluded != null && plan.seatsConfigured && (
              <div className="fl-signup-seats">
                <div>
                  <strong>Extra seats</strong>
                  <span>{seatsIncluded} included with {plan.name} · +{fmtUsd(seatPrice)}/seat/mo</span>
                </div>
                <div className="fl-stepper" role="group" aria-label="Extra seats">
                  <button type="button" onClick={() => select(planKey, annual, Math.max(0, seats - 1))} disabled={seats === 0}>−</button>
                  <span className="u-tabular">{seats}</span>
                  <button type="button" onClick={() => select(planKey, annual, seats + 1)}>+</button>
                </div>
              </div>
            )}

            {/* Order summary */}
            <div className="fl-signup-summary2">
              {plan ? (
                <>
                  <div className="fl-signup-sumrow">
                    <span>{plan.name} license ({annual ? 'annual' : 'monthly'})</span>
                    <span>{fmtUsd(monthly)}/mo</span>
                  </div>
                  {seats > 0 && (
                    <div className="fl-signup-sumrow">
                      <span>{seats} extra seat{seats === 1 ? '' : 's'} × {fmtUsd(seatPrice)}</span>
                      <span>{fmtUsd(seats * seatPrice)}/mo</span>
                    </div>
                  )}
                  <div className="fl-signup-sumrow total">
                    <span>Total</span>
                    <span>
                      {plan.fromPrice ? 'from ' : ''}{fmtUsd(total)}/mo
                      {annual ? ` · ${fmtUsd(total * 12)} billed yearly` : ''}
                    </span>
                  </div>
                  <p className="fl-signup-sumnote">
                    Recurring subscription — cancel anytime.
                    {planTrialDays > 0 ? ` Starts with a ${planTrialDays}-day free trial; first charge after the trial.` : ''}
                    {' '}<strong>Nothing is charged now</strong> — you’ll confirm at a Stripe checkout after
                    creating your account and workspace. On-demand AI beyond the included tokens comes from
                    one-time token packs (never expire), not overage bills.
                  </p>
                </>
              ) : (
                <p className="fl-signup-sumnote">
                  <strong>No payment today.</strong> Your workspace starts on the {trial?.days || 14}-day trial —
                  pick a plan from Billing whenever you’re ready, or buy one-time token packs as you go.
                </p>
              )}
            </div>
          </section>

          {/* Account form */}
          <section className="fl-signup-form">
            <h2>Create account</h2>
            {form}
            <p className="fl-signup-fineprint">
              <Icon name="shield" size={12} /> Your documents live in your organization’s workspace behind
              role-based access; every version is hash-sealed and audited.
            </p>
          </section>
        </div>
        )}
      </div>
    );
  }

  // ---- Compact modal: sign-in, reset, invited signup ----
  return (
    <div className="cs-modal" style={{ maxWidth: docs ? 880 : 400 }}>
      <div className="cs-brand" style={{ fontSize: 28, marginBottom: 6 }}>
        {process.env.REACT_APP_APP_NAME || 'fast.legal'}
      </div>
      <h2 style={{ fontSize: 22, marginTop: 0 }}>
        {docs ? 'Agreements' : mode === 'signup' ? 'Create account' : mode === 'reset' ? 'Reset password' : 'Sign in'}
      </h2>

      {mode === 'reset' && !notice && (
        <p className="u-small u-muted" style={{ marginTop: 0 }}>
          Enter your account email and we'll send you a link to choose a new password.
        </p>
      )}

      {invitedEmail && !docs && !error && !notice && (
        <div className="cs-empty" style={{ padding: 12, marginBottom: 14 }}>
          You've been invited — {mode === 'signup'
            ? 'create your account with this email and you\u2019ll join your organization automatically.'
            : 'sign in with this email to join your organization.'}
        </div>
      )}

      {form}
    </div>
  );
}
